import { useEffect, useState } from 'react';
import axios from 'axios';
import vopayApi from '@/services/api.config';
import {
  ZelleWithdrawTransactionResponse,
  ZelleWithdrawRequest,
  ZelleWithdrawResponse,
  ZelleRecipientsRequest,
  ZelleRecipientsResponse,
  ZelleCancelRequest,
  ZelleCancelResponse,
} from 'vopay';

const { api } = vopayApi;

// Zelle Hooks
export function useZelle({ bearer }: { bearer?: string } = {}) {
  const source = axios.CancelToken.source();

  // Transaction
  const [transaction, setTransaction] = useState<ZelleWithdrawTransactionResponse>();
  const [transactionLoading, setTransactionLoading] = useState(false);

  // Withdraw
  const [withdrawLoading, setWithdrawLoading] = useState(false);

  // Cancel
  const [cancelLoading, setCancelLoading] = useState(false);

  // Recipients
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipients, setRecipients] = useState<ZelleRecipientsResponse>();

  useEffect(() => {
    return () => {
      source.cancel();
    };
  }, []);

  async function fetchZelleWithdrawTransaction(transactionId: string) {
    try {
      setTransactionLoading(true);
      const res = await api.fetchZelleWithdrawTransaction({ TransactionID: transactionId }, bearer, source);
      if (!res.Success) {
        setTransaction(undefined);
        throw new Error(res.ErrorMessage);
      }
      setTransaction(res);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setTransactionLoading(false);
    }
  }

  async function zelleWithdraw(params: ZelleWithdrawRequest): Promise<ZelleWithdrawResponse> {
    try {
      setWithdrawLoading(true);
      const res = await api.zelleWithdraw(params, source);
      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setWithdrawLoading(false);
    }
  }

  async function cancelZelleWithdraw(params: ZelleCancelRequest): Promise<ZelleCancelResponse> {
    try {
      setCancelLoading(true);
      const res = await api.zelleCancel(params, source);
      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setCancelLoading(false);
    }
  }

  async function fetchZelleRecipients(params: ZelleRecipientsRequest = {}) {
    try {
      setRecipientsLoading(true);
      const res = await api.fetchZelleRecipients(params, source);
      if (!res.Success) {
        setRecipients(undefined);
        throw new Error(res.ErrorMessage);
      }
      setRecipients(res);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setRecipientsLoading(false);
    }
  }

  return {
    // Transaction
    transaction,
    transactionLoading,
    fetchZelleWithdrawTransaction,

    // Withdraw
    withdrawLoading,
    zelleWithdraw,

    // Cancel
    cancelLoading,
    cancelZelleWithdraw,

    // Recipients
    recipientsLoading,
    recipients,
    fetchZelleRecipients,
  };
}

// Default export for backward compatibility
export default useZelle;
